import { useMemo } from 'react';
import * as THREE from 'three';

const Column = ({ inputs, convRatio, color, drawingBaseDimensions }) => {
  const outerRadius = (drawingBaseDimensions.column_diameter * convRatio) / 2;
  const thickness = outerRadius * 0.05;
  const innerRadius = outerRadius - thickness;
  const feetHeight = 120 * convRatio;
  const footcrossSection = 120 * convRatio;
  const footcrossLength = drawingBaseDimensions.footprint_diameter * convRatio;
  const posTop =
    (drawingBaseDimensions.h3 +
      drawingBaseDimensions.column_top_extension +
      150) *
    convRatio;
  const posBot = feetHeight + footcrossSection;
  const height = posTop - posBot;
  const footWidth = 175 * convRatio;
  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;
  const angleFoot =
    reverseConfig *
    THREE.MathUtils.degToRad(
      (drawingBaseDimensions.configuration_angle + 45) % 90
    );
  const inclinationLe =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_le) *
    reverseConfig;
  const inclinationLe_db = inclinationLe / 2;
  const heightDiffLE =
    (Math.tan(inclinationLe) *
      reverseConfig *
      drawingBaseDimensions.le_angular +
      Math.tan(inclinationLe_db) *
        drawingBaseDimensions.le_double_bend_value *
        reverseConfig) *
    convRatio;
  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const inclination_db = inclination / 2;
  const heightDiffUE =
    (Math.tan(inclination) * reverseConfig * drawingBaseDimensions.ue_angular +
      Math.tan(inclination_db) *
        drawingBaseDimensions.ue_double_bend_value *
        reverseConfig) *
    convRatio;

  const spokesPerWinding = drawingBaseDimensions.nbr_spoke;
  const windingCount = drawingBaseDimensions.nbr_windings;
  const spokeDiameter = drawingBaseDimensions.spoke_diameter * convRatio;
  const spokeRadius = spokeDiameter / 2;
  const windingThickness = drawingBaseDimensions.winding_thickness * convRatio;
  const outerGuideRadius =
    drawingBaseDimensions.column_centre_to_inside_outer_guide * convRatio;
  const spokeEndRadius = outerGuideRadius + 20 * convRatio;
  const spokeLength = Math.max(0, spokeEndRadius - outerRadius);
  const spokeCenterRadius = outerRadius + spokeLength / 2;
  const columnCenterY = (posTop + posBot) / 2;

  // Centralized material
  const material = useMemo(
    () => (
      <meshStandardMaterial
        color={color}
        roughness={0.8}
        metalness={0.5}
        side={THREE.DoubleSide}
      />
    ),
    [color]
  );

  const cylinderGeometry = useMemo(() => {
    return new THREE.CylinderGeometry(
      outerRadius,
      outerRadius,
      height,
      32,
      1,
      true
    );
  }, [outerRadius, height]);

  const ringGeometry = useMemo(() => {
    return new THREE.RingGeometry(innerRadius, outerRadius, 32, 1);
  }, [innerRadius, outerRadius]);

  const spokeGeometry = useMemo(() => {
    return new THREE.CylinderGeometry(
      spokeRadius,
      spokeRadius,
      spokeLength,
      16
    );
  }, [spokeRadius, spokeLength]);

  const spokeYConfig =
    drawingBaseDimensions.special_return === 'Y Configuration'
      ? 85 * convRatio
      : 0;

  const spokeInstances = useMemo(() => {
    const minYAbs =
      drawingBaseDimensions.h1 * convRatio -
      windingThickness -
      spokeRadius +
      heightDiffLE +
      spokeYConfig;
    const maxYAbs =
      drawingBaseDimensions.h3 * convRatio -
      windingThickness -
      spokeRadius -
      heightDiffUE +
      spokeYConfig;
    const totalSpokes = windingCount * spokesPerWinding + 1;

    if (totalSpokes <= 1) {
      return [
        {
          y: (minYAbs + maxYAbs) / 2 - columnCenterY,
          angle: 0,
        },
      ];
    }

    return Array.from({ length: totalSpokes }, (_, index) => {
      const t = index / (totalSpokes - 1);
      const yAbs = minYAbs + (maxYAbs - minYAbs) * t;
      return {
        y: yAbs - columnCenterY,
        angle: -reverseConfig * 2 * Math.PI * windingCount * t,
      };
    });
  }, [
    windingCount,
    spokesPerWinding,
    drawingBaseDimensions.h1,
    drawingBaseDimensions.h3,
    convRatio,
    windingThickness,
    spokeRadius,
    columnCenterY,
    heightDiffLE,
    heightDiffUE,
    reverseConfig,
  ]);

  const Spoke = ({ y, angle }) => (
    <group rotation={[0, angle, 0]}>
      <mesh
        geometry={spokeGeometry}
        position={[spokeCenterRadius, y, 0]}
        rotation={[0, 0, -Math.PI / 2]}
      >
        {material}
      </mesh>
    </group>
  );

  const HollowSquareFoot = ({
    position,
    rotation,
    footcrossSection,
    footcrossLength,
  }) => {
    const wallThickness = footcrossSection * 0.05;

    return (
      <group position={position} rotation={rotation}>
        {/* Top wall */}
        <mesh position={[0, footcrossSection / 2 - wallThickness / 2, 0]}>
          <boxGeometry
            args={[footcrossSection, wallThickness, footcrossLength]}
          />
          {material}
        </mesh>
        {/* Bottom wall */}
        <mesh position={[0, -footcrossSection / 2 + wallThickness / 2, 0]}>
          <boxGeometry
            args={[footcrossSection, wallThickness, footcrossLength]}
          />
          {material}
        </mesh>
        {/* Left wall */}
        <mesh position={[-footcrossSection / 2 + wallThickness / 2, 0, 0]}>
          <boxGeometry
            args={[
              wallThickness,
              footcrossSection - 2 * wallThickness,
              footcrossLength,
            ]}
          />
          {material}
        </mesh>
        {/* Right wall */}
        <mesh position={[footcrossSection / 2 - wallThickness / 2, 0, 0]}>
          <boxGeometry
            args={[
              wallThickness,
              footcrossSection - 2 * wallThickness,
              footcrossLength,
            ]}
          />
          {material}
        </mesh>
      </group>
    );
  };

  return (
    <group position={[0, (posTop + posBot) / 2, 0]}>
      <mesh geometry={cylinderGeometry}>{material}</mesh>
      <mesh
        geometry={ringGeometry}
        position={[0, height / 2, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        {material}
      </mesh>
      <mesh
        geometry={ringGeometry}
        position={[0, -height / 2, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        {material}
      </mesh>
      {spokeLength > 0 &&
        spokeInstances.map((spoke, index) => (
          <Spoke key={`spoke-${index}`} y={spoke.y} angle={spoke.angle} />
        ))}
      <HollowSquareFoot
        position={[0, -height / 2 - footcrossSection / 2, 0]}
        rotation={[0, -(angleFoot + (reverseConfig * Math.PI) / 2), 0]}
        footcrossSection={footcrossSection}
        footcrossLength={footcrossLength}
      />
      <HollowSquareFoot
        position={[0, -height / 2 - footcrossSection / 2, 0]}
        rotation={[0, -angleFoot, 0]}
        footcrossSection={footcrossSection}
        footcrossLength={footcrossLength}
      />
      {[
        {
          x: (-footcrossLength / 2 + footWidth / 2) * Math.cos(angleFoot),
          z: (-footcrossLength / 2 + footWidth / 2) * Math.sin(angleFoot),
        },
        {
          x: (footcrossLength / 2 - footWidth / 2) * Math.cos(angleFoot),
          z: (footcrossLength / 2 - footWidth / 2) * Math.sin(angleFoot),
        },
        {
          x: footWidth * Math.cos(angleFoot),
          z: footWidth * Math.sin(angleFoot),
        },
        {
          x: -footWidth * Math.cos(angleFoot),
          z: -footWidth * Math.sin(angleFoot),
        },
        {
          x:
            (-footcrossLength / 2 + footWidth / 2) *
            Math.cos(angleFoot + (reverseConfig * Math.PI) / 2),
          z:
            (-footcrossLength / 2 + footWidth / 2) *
            Math.sin(angleFoot + (reverseConfig * Math.PI) / 2),
        },
        {
          x:
            (footcrossLength / 2 - footWidth / 2) *
            Math.cos(angleFoot + (reverseConfig * Math.PI) / 2),
          z:
            (footcrossLength / 2 - footWidth / 2) *
            Math.sin(angleFoot + (reverseConfig * Math.PI) / 2),
        },
      ].map((dim, index) => (
        <Foot
          key={`foot-${index}-${dim.x.toFixed(3)}-${dim.z.toFixed(3)}`}
          position={[
            dim.x,
            -height / 2 - footcrossSection - (3 * feetHeight) / 4,
            dim.z,
          ]}
          isSquare={
            inputs.feet === 'Welded feet for heavy duty -60/+90 mm adjustable'
          }
          convRatio={convRatio}
          feetHeight={feetHeight}
          flatWidth={footWidth}
        />
      ))}
    </group>
  );
};

const Foot = ({
  position,
  convRatio,
  isSquare = true,
  feetHeight,
  flatWidth,
}) => {
  const columnRadius = 10 * convRatio;
  const flatThickness = 6 * convRatio;

  const columnGeometry = useMemo(() => {
    return new THREE.CylinderGeometry(
      columnRadius,
      columnRadius,
      feetHeight,
      32
    );
  }, [columnRadius, feetHeight]);

  const flatGeometry = useMemo(() => {
    if (isSquare) {
      const shape = new THREE.Shape();
      shape.moveTo(-flatWidth / 2, -flatWidth / 2);
      shape.lineTo(flatWidth / 2, -flatWidth / 2);
      shape.lineTo(flatWidth / 2, flatWidth / 2);
      shape.lineTo(-flatWidth / 2, flatWidth / 2);
      shape.lineTo(-flatWidth / 2, -flatWidth / 2);
      return new THREE.ExtrudeGeometry(shape, {
        depth: flatThickness,
        bevelEnabled: false,
      });
    } else {
      return new THREE.CylinderGeometry(
        flatWidth / 2,
        flatWidth / 2,
        flatThickness,
        32
      );
    }
  }, [isSquare, flatWidth, flatThickness]);

  return (
    <group position={position}>
      <mesh geometry={columnGeometry} position={[0, feetHeight / 2, 0]}>
        <meshStandardMaterial
          color="silver"
          roughness={0.8}
          metalness={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh
        geometry={flatGeometry}
        position={[0, 0, 0]}
        rotation={[isSquare ? Math.PI / 2 : 0, 0, 0]}
      >
        <meshStandardMaterial
          color="black"
          roughness={0.8}
          metalness={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
};

export default Column;
